// PAGE-MCH-USER-004 —— 企业用户开户与 KYB 审核（S9 合同交付 4：扩展替换旧 /corps 台账数据源）
// 路由：/merchant/users/corporates/onboarding
//
// 契约：ONB-001 列表/详情 + ONB-002 四决策（APPROVE/REJECT/SUPPLEMENT/ESCALATE）+
//       ONB-005 平台备案重试（4514 retryable，本地开通不阻塞）。
// 红线：注册号仅掩码（regNoMask 原样渲染，后端完整值不落页面）；执照/授权书影像仅
//       objectKey 经既有文件通道灯箱预览，可见辅文案=槽位名，无原件文件名/无导出入口；
//       预览失败仅警告不禁用审核操作（对齐 KYC 审核页先例，R5）。
// 裁决落地：platformStatus 筛选走前端本地过滤（ONB-001 无该后端参数，Edge #7）；
//       幂等键：决策弹窗打开时重置，最终提交生成一次，失败重置（R6，防同 Key 异体 1009）。
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  Building2,
  ChevronLeft,
  ChevronRight,
  Clock,
  RefreshCw,
  Search,
  ShieldAlert,
  X,
} from 'lucide-react';
import PageContainer from '@/components/PageContainer';
import EmptyState from '@/components/EmptyState';
import StatusBadge from '@/components/StatusBadge';
import { Modal } from '@/components/Modal';
import { CredentialLightbox } from '@/components/CredentialLightbox';
import { useToast } from '@/components/Toast';
import { fetchMerchantFileContent } from '@/api/files';
import {
  decideCorporateOnboarding,
  getCorporateOnboarding,
  isForbiddenError,
  listCorporateOnboardings,
  mapDecisionErrorToMessage,
  mapDocStatusToUi,
  mapFilingErrorToMessage,
  mapOnboardingStatusToUi,
  mapPlatformStatusToUi,
  retryCorporateFiling,
  type CorporateDecisionBody,
  type CorporateOnboardingDetail,
  type CorporateOnboardingItem,
} from '@/api/corporate';
import { formatDateTime } from '@shared/formats/format';

type DecisionAction = CorporateDecisionBody['decision'];

const STATUS_OPTIONS = [
  { value: 'PENDING_APPROVAL', label: '商户待审' },
  { value: 'SUBMITTED', label: '已提交' },
  { value: 'PENDING_RESUBMIT', label: '待补件' },
  { value: 'APPROVED', label: '商户已通过' },
  { value: 'REJECTED', label: '已驳回' },
  { value: 'FILING', label: '平台备案中' },
  { value: 'FILED', label: '平台已备案' },
  { value: 'ESCALATED', label: '转平台终审' },
  { value: 'CANCELLED', label: '已撤销' },
  { value: 'DRAFT', label: '草稿' },
] as const;

const PLATFORM_OPTIONS = [
  { value: '', label: '全部' },
  { value: 'NONE', label: '未备案' },
  { value: 'FILING', label: '备案中' },
  { value: 'FILED', label: '已备案' },
] as const;

const DECISION_META: Record<DecisionAction, { title: string; confirmText: string; buttonClass: string }> = {
  APPROVE: { title: 'KYB 审核通过', confirmText: '确认通过', buttonClass: 'btn-primary' },
  REJECT: { title: 'KYB 审核驳回', confirmText: '确认驳回', buttonClass: 'btn-danger' },
  SUPPLEMENT: { title: '发起补件', confirmText: '确认补件', buttonClass: 'btn-primary' },
  ESCALATE: { title: '转平台审核', confirmText: '确认转平台', buttonClass: 'btn-primary' },
};

const PAGE_SIZE = 20;

/** 详情字段只读行。 */
function FieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-3 py-1.5">
      <span className="text-xs text-slate-400 shrink-0">{label}</span>
      <span className="text-sm text-slate-700 text-right break-all">{children}</span>
    </div>
  );
}

/**
 * 企业影像槽位预览（R5）：objectKey 经既有 /files/content 通道拉 blob 灯箱；
 * 通道不可用 → 槽位名占位 + 警示（禁用决策主流程）。可见辅文案=槽位名，
 * 不展示对象键值 / 原始文件名，不提供原件导出入口。
 */
function CorporateFilePreview({ objectKey, slotLabel }: { objectKey?: string | null; slotLabel: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [state, setState] = useState<'empty' | 'loading' | 'ready' | 'error'>('empty');
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    if (!objectKey) {
      setState('empty');
      setSrc(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    setState('loading');
    void fetchMerchantFileContent(objectKey).then((res) => {
      if (cancelled) return;
      if (res.ok) {
        objectUrl = URL.createObjectURL(res.blob);
        setSrc(objectUrl);
        setState('ready');
      } else {
        setState('error');
      }
    });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [objectKey]);

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <p className="text-[11px] text-slate-400 mb-2">{slotLabel}</p>
      {state === 'empty' && (
        <div className="py-6 text-center text-xs text-slate-400">未上传</div>
      )}
      {state === 'loading' && <p className="text-xs text-slate-400 py-6 text-center">影像加载中…</p>}
      {state === 'error' && (
        <div className="flex items-start gap-2 text-xs text-amber-800 py-4">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <p>影像预览暂不可用，仍可正常审核（已按原件留痕归档）。</p>
        </div>
      )}
      {state === 'ready' && src && (
        <button type="button" className="block w-full cursor-pointer" onClick={() => setLightbox(true)}>
          <img src={src} alt={slotLabel} className="max-h-44 mx-auto rounded object-contain" />
        </button>
      )}
      <CredentialLightbox open={lightbox} src={src} alt={slotLabel} onClose={() => setLightbox(false)} />
    </div>
  );
}

export default function CorporateKybReviewPage() {
  const toast = useToast();
  const [searchParams] = useSearchParams();

  // ---- 列表（ONB-001） ----
  const [rows, setRows] = useState<CorporateOnboardingItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  // 筛选默认 PENDING_APPROVAL（AC-PAGE-004-01 主路径）
  const [statusFilter, setStatusFilter] = useState('PENDING_APPROVAL');
  // Edge #7：platformStatus 后端无查询参数 → 本地过滤（当前页行字段）
  const [platformFilter, setPlatformFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // ---- 详情抽屉 ----
  const [detail, setDetail] = useState<CorporateOnboardingDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // ---- 决策弹窗（R6：幂等键随弹窗生命周期重置） ----
  const [decision, setDecision] = useState<DecisionAction | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const decisionKeyRef = useRef<string | null>(null);

  const [retrying, setRetrying] = useState(false);
  const retryKeyRef = useRef<string | null>(null);

  const forbidden = !!loadError && isForbiddenError(loadError);

  const loadList = useCallback(
    async (nextPage: number) => {
      setLoading(true);
      setLoadError(null);
      const res = await listCorporateOnboardings({
        status: statusFilter,
        page: nextPage,
        pageSize: PAGE_SIZE,
      });
      setLoading(false);
      if (!res.ok) {
        setLoadError(res.error || '加载失败');
        setRows([]);
        setTotal(0);
        return;
      }
      setRows(res.data.items ?? []);
      setTotal(Number(res.data.total) || 0);
      setPage(res.data.page || nextPage);
    },
    [statusFilter],
  );

  const openDetail = useCallback(async (applicationId: string) => {
    setDetailLoading(true);
    setDetailError(null);
    setDetail(null);
    const res = await getCorporateOnboarding(applicationId);
    setDetailLoading(false);
    if (!res.ok) {
      setDetailError(res.error || '加载失败');
      return;
    }
    setDetail(res.data);
  }, []);

  useEffect(() => {
    void loadList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // PAGE-009「去审核」定位：?applicationId= → 打开该申请详情；否则默认筛选已定位待审
  const focusId = searchParams.get('applicationId');
  const focusLoadedRef = useRef(false);
  useEffect(() => {
    if (focusId && !focusLoadedRef.current) {
      focusLoadedRef.current = true;
      void openDetail(focusId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId]);

  // Edge #7：platformStatus 本地过滤（基于当前页行字段，不发明后端参数）
  const visibleRows = platformFilter
    ? rows.filter((r) => (r.platformStatus || 'NONE').toUpperCase() === platformFilter)
    : rows;

  const openDecision = (action: DecisionAction) => {
    decisionKeyRef.current = null; // R6：弹窗打开即重置，旧表单指纹不复用
    setReason('');
    setDecision(action);
  };

  const closeDecision = () => {
    if (submitting) return;
    setDecision(null);
    setReason('');
  };

  // Edge #5：REJECT/SUPPLEMENT 原因必填 1-500，前端拦截不发请求；APPROVE/ESCALATE 无需原因
  const validateReason = (action: DecisionAction): string | null => {
    if (action !== 'REJECT' && action !== 'SUPPLEMENT') return null;
    const trimmed = reason.trim();
    if (!trimmed) return '请填写原因（1-500 字）';
    if (trimmed.length > 500) return '原因不能超过 500 字';
    return null;
  };

  const reloadAfterWrite = async (applicationId: string) => {
    await loadList(page);
    if (detail?.applicationId === applicationId) {
      await openDetail(applicationId);
    }
  };

  const submitDecision = async () => {
    if (!detail || !decision || submitting) return;
    const reasonError = validateReason(decision);
    if (reasonError) {
      toast.error(reasonError);
      return;
    }
    setSubmitting(true);
    if (!decisionKeyRef.current) {
      decisionKeyRef.current = crypto.randomUUID();
    }
    const body: CorporateDecisionBody = {
      decision,
      ...(decision === 'REJECT' || decision === 'SUPPLEMENT' ? { reason: reason.trim() } : {}),
    };
    const res = await decideCorporateOnboarding(detail.applicationId, body, {
      idempotencyKey: decisionKeyRef.current,
    });
    setSubmitting(false);
    if (!res.ok) {
      // R6：失败即弃键——表单若变更后重提，不复用旧键（防同 Key 异体 1009）
      decisionKeyRef.current = null;
      toast.error(mapDecisionErrorToMessage(res.error));
      return;
    }
    const data = res.data;
    if (decision === 'APPROVE') {
      toast.success(
        `审核通过，企业主账户已开通（corpId: ${data.corpId ?? '—'} / corpNo: ${data.corpNo ?? '—'}）`,
      );
    } else if (decision === 'ESCALATE') {
      toast.info('已转平台终审，等待平台审核结论');
    } else if (decision === 'REJECT') {
      toast.success('已驳回，原因将通知企业');
    } else {
      toast.success('已发起补件，等待企业补充材料');
    }
    setDecision(null);
    setReason('');
    decisionKeyRef.current = null;
    await reloadAfterWrite(detail.applicationId);
  };

  const submitFilingRetry = async () => {
    if (!detail || retrying) return;
    setRetrying(true);
    if (!retryKeyRef.current) {
      retryKeyRef.current = crypto.randomUUID();
    }
    const res = await retryCorporateFiling(detail.applicationId, {
      idempotencyKey: retryKeyRef.current,
    });
    setRetrying(false);
    if (!res.ok) {
      retryKeyRef.current = null;
      // 4514 retryable：状态不变，toast 提示可重试（AC-PAGE-004-05）
      toast.error(mapFilingErrorToMessage(res.error));
      return;
    }
    retryKeyRef.current = null;
    toast.success(`平台备案上报成功（status: ${res.data.status}）`);
    await reloadAfterWrite(detail.applicationId);
  };

  // 备案待办：申请已过审但平台备案未完成（FILING 挂起待重试）
  const filingPending = (status: string, platformStatus: string | null) =>
    status === 'FILING' || (platformStatus || '').toUpperCase() === 'FILING';

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const canDecide = detail?.status === 'PENDING_APPROVAL';
  const showFilingTodo = detail ? filingPending(detail.status, detail.platformStatus) : false;

  return (
    <PageContainer
      title="企业开户审核"
      subtitle="企业 KYB 准入审核（营业执照 / 法人 / 授权书 / UBO）· 平台备案跟踪"
      extra={
        <button
          type="button"
          className="btn-secondary"
          onClick={() => void loadList(page)}
          disabled={loading}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> 刷新
        </button>
      }
    >
      <div className="space-y-4">
        <section className="card">
          {/* 筛选行：status 默认 PENDING_APPROVAL；platformStatus 本地过滤 */}
          <div className="flex flex-wrap items-end gap-3 px-5 pt-5">
            <div>
              <label className="label">状态</label>
              <select
                className="input w-44"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">平台备案</label>
              <select
                className="input w-36"
                value={platformFilter}
                onChange={(e) => setPlatformFilter(e.target.value)}
              >
                {PLATFORM_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => void loadList(1)}
              disabled={loading}
            >
              <Search className="w-4 h-4" /> 查询
            </button>
            <p className="text-[11px] text-slate-400 pb-2">
              平台备案筛选为当前页内过滤
            </p>
          </div>

          {loading ? (
            <div className="p-8 space-y-3">
              <div className="h-6 rounded bg-slate-100 animate-pulse" />
              <div className="h-6 rounded bg-slate-100 animate-pulse" />
              <div className="h-6 rounded bg-slate-100 animate-pulse" />
            </div>
          ) : loadError ? (
            <div className="p-6">
              <EmptyState
                message={forbidden ? '无权限访问' : '加载失败'}
                description={forbidden ? '仅商户管理员/财务可执行 KYB 审核' : loadError}
                action={
                  !forbidden && (
                    <button type="button" className="btn-secondary" onClick={() => void loadList(1)}>
                      重试
                    </button>
                  )
                }
              />
            </div>
          ) : visibleRows.length === 0 ? (
            <div className="p-6">
              <EmptyState message="暂无数据" description="当前筛选条件下没有开户申请" />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto mt-3">
                <table className="table-auto w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                      <th className="px-5 py-3 font-medium">企业名</th>
                      <th className="px-5 py-3 font-medium">注册号</th>
                      <th className="px-5 py-3 font-medium">材料齐备</th>
                      <th className="px-5 py-3 font-medium">平台备案</th>
                      <th className="px-5 py-3 font-medium">状态</th>
                      <th className="px-5 py-3 font-medium">提交时间</th>
                      <th className="px-5 py-3 font-medium">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row) => (
                      <tr key={row.applicationId} className="border-b border-slate-50 last:border-0">
                        <td className="px-5 py-3 font-medium text-slate-900">
                          {row.corpName || '—'}
                          {filingPending(row.status, row.platformStatus) && (
                            <span className="ml-2 inline-flex items-center gap-0.5 rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 text-[11px]">
                              <ShieldAlert className="w-3 h-3" /> 备案待办
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3 font-mono text-slate-500">{row.regNoMask || '—'}</td>
                        <td className="px-5 py-3">
                          {(row.docStatus || '').toUpperCase() === 'INCOMPLETE' ? (
                            <div>
                              <span className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 px-2 py-0.5 text-xs font-medium">
                                <AlertTriangle className="w-3 h-3" /> 缺件
                              </span>
                              {(row.resubmitFields ?? []).length > 0 && (
                                <p className="text-[11px] text-red-500 mt-1">
                                  待补：{row.resubmitFields!.join('、')}
                                </p>
                              )}
                            </div>
                          ) : (
                            <StatusBadge status={row.docStatus} label={mapDocStatusToUi(row.docStatus)} />
                          )}
                        </td>
                        <td className="px-5 py-3">
                          <StatusBadge
                            status={row.platformStatus || 'NONE'}
                            label={mapPlatformStatusToUi(row.platformStatus)}
                          />
                        </td>
                        <td className="px-5 py-3">
                          <StatusBadge status={row.status} label={mapOnboardingStatusToUi(row.status)} />
                        </td>
                        <td className="px-5 py-3 text-slate-500">{formatDateTime(row.submittedAt)}</td>
                        <td className="px-5 py-3">
                          <button
                            type="button"
                            className="btn-ghost px-2 py-1 text-xs text-slate-600"
                            onClick={() => void openDetail(row.applicationId)}
                          >
                            查看详情
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
                <span>共 {total} 条申请</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="btn-ghost px-2 py-1 text-xs"
                    disabled={page <= 1 || loading}
                    onClick={() => void loadList(page - 1)}
                  >
                    <ChevronLeft className="w-4 h-4" /> 上一页
                  </button>
                  <span>
                    {page} / {totalPages}
                  </span>
                  <button
                    type="button"
                    className="btn-ghost px-2 py-1 text-xs"
                    disabled={page >= totalPages || loading}
                    onClick={() => void loadList(page + 1)}
                  >
                    下一页 <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      {/* ============ 详情抽屉（右侧滑出） ============ */}
      {detailLoading && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" />
          <div className="relative bg-white w-[560px] max-w-full h-full p-6 animate-in">
            <p className="text-sm text-slate-400">加载申请详情…</p>
          </div>
        </div>
      )}
      {!detailLoading && detailError && (
        <Modal open title="申请详情加载失败" onClose={() => setDetailError(null)} width="420px">
          <p className="text-sm text-slate-600">{detailError}</p>
        </Modal>
      )}
      {!detailLoading && detail && (
        <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            onClick={() => setDetail(null)}
          />
          <div className="relative bg-white w-[600px] max-w-full h-full overflow-y-auto shadow-pop">
            {/* 抽屉头 */}
            <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-start justify-between gap-3 z-[1]">
              <div>
                <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-400" /> {detail.corpName || '企业开户申请'}
                </h3>
                <p className="text-xs text-slate-400 mt-0.5 font-mono">
                  {detail.applicationNo || detail.applicationId}
                </p>
              </div>
              <button
                className="text-slate-400 hover:text-slate-600"
                onClick={() => setDetail(null)}
                aria-label="关闭"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-5">
              {/* 状态概览 */}
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge status={detail.status} label={mapOnboardingStatusToUi(detail.status)} />
                <StatusBadge
                  status={detail.platformStatus || 'NONE'}
                  label={`平台备案：${mapPlatformStatusToUi(detail.platformStatus)}`}
                />
                <StatusBadge
                  status={detail.docStatus}
                  label={mapDocStatusToUi(detail.docStatus)}
                />
                {detail.needPlatformReview && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 text-purple-700 px-2 py-0.5 text-xs font-medium">
                    <ShieldAlert className="w-3 h-3" /> 高风险行业 · 须平台终审
                  </span>
                )}
                {showFilingTodo && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 text-xs font-medium">
                    <ShieldAlert className="w-3 h-3" /> 平台备案待办
                  </span>
                )}
              </div>

              {detail.rejectReasonPublic && (
                <div className="rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-xs text-red-700">
                  驳回原因（对企可见）：{detail.rejectReasonPublic}
                </div>
              )}

              {/* 基本信息 */}
              <div className="card p-4">
                <h4 className="text-xs font-semibold text-slate-500 mb-2">企业信息</h4>
                <FieldRow label="企业法定名称">{detail.legalName || detail.corpName || '—'}</FieldRow>
                <FieldRow label="统一社会信用代码 / 注册号">
                  <span className="font-mono">{detail.regNoMask || '—'}</span>
                </FieldRow>
                <FieldRow label="法域">{detail.jurisdiction || '—'}</FieldRow>
                <FieldRow label="法人姓名">{detail.legalPersonName || '—'}</FieldRow>
                <FieldRow label="法人证件类型">{detail.legalPersonIdType || '—'}</FieldRow>
                <FieldRow label="提交时间">{formatDateTime(detail.submittedAt)}</FieldRow>
                <FieldRow label="法人 KYC">
                  {detail.legalPersonKycCaseId ? `已关联（case ${detail.legalPersonKycCaseId}）` : '—'}
                </FieldRow>
                <FieldRow label="经办人 KYC">
                  {detail.contactKycCaseId ? `已关联（case ${detail.contactKycCaseId}）` : '—'}
                </FieldRow>
              </div>

              {/* 影像槽位：辅文案=槽位名；预览失败仅警告不禁用审核（R5） */}
              <div className="grid grid-cols-2 gap-3">
                <CorporateFilePreview objectKey={detail.licenseFileId} slotLabel="营业执照" />
                <CorporateFilePreview
                  objectKey={detail.authorizationFileId}
                  slotLabel="企业授权书"
                />
              </div>

              {/* UBO 穿透声明 */}
              <div className="card p-4">
                <h4 className="text-xs font-semibold text-slate-500 mb-2">UBO 穿透声明</h4>
                {(detail.uboDocument ?? []).length === 0 ? (
                  <p className="text-xs text-slate-400">无登记记录</p>
                ) : (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-slate-400 border-b border-slate-100">
                        <th className="py-1.5 font-medium">姓名</th>
                        <th className="py-1.5 font-medium">证件（掩码）</th>
                        <th className="py-1.5 font-medium">持股比例</th>
                        <th className="py-1.5 font-medium">控制人</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.uboDocument ?? []).map((ubo, i) => (
                        <tr key={`${ubo.name}-${i}`} className="border-b border-slate-50 last:border-0">
                          <td className="py-1.5 text-slate-700">{ubo.name || '—'}</td>
                          <td className="py-1.5 font-mono text-slate-500">{ubo.idNoMask || '—'}</td>
                          <td className="py-1.5 text-slate-700">
                            {ubo.shareRatio ? `${ubo.shareRatio}%` : '—'}
                          </td>
                          <td className="py-1.5">
                            {ubo.controlFlag ? (
                              <span className="badge-info">是</span>
                            ) : (
                              <span className="text-slate-400">否</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* 审核时间线（WORM 投影） */}
              <div className="card p-4">
                <h4 className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" /> 审核时间线
                </h4>
                {(detail.timeline ?? []).length === 0 ? (
                  <p className="text-xs text-slate-400">暂无记录</p>
                ) : (
                  <ol className="space-y-2">
                    {(detail.timeline ?? []).map((t, i) => (
                      <li key={i} className="flex gap-2 text-xs">
                        <span className="mt-1 w-1.5 h-1.5 rounded-full bg-brand-500 shrink-0" />
                        <div>
                          <p className="text-slate-700">
                            {mapOnboardingStatusToUi(t.status)}
                            <span className="text-slate-400 ml-2">{formatDateTime(t.at)}</span>
                            {t.operatorType && (
                              <span className="text-slate-400 ml-2">· {t.operatorType}</span>
                            )}
                          </p>
                          {t.remark && <p className="text-slate-400 mt-0.5">{t.remark}</p>}
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              {/* 操作区：四决策（仅商户待审）+ 备案重试 */}
              {showFilingTodo && (
                <button
                  type="button"
                  className="btn-secondary w-full"
                  disabled={retrying}
                  onClick={() => void submitFilingRetry()}
                >
                  <RefreshCw className={`w-4 h-4 ${retrying ? 'animate-spin' : ''}`} />
                  {retrying ? '重新上报中…' : '重新上报平台备案'}
                </button>
              )}
              {canDecide ? (
                <div className="grid grid-cols-2 gap-2 pb-6">
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => openDecision('APPROVE')}
                  >
                    通过
                  </button>
                  <button
                    type="button"
                    className="btn-danger"
                    onClick={() => openDecision('REJECT')}
                  >
                    驳回
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => openDecision('SUPPLEMENT')}
                  >
                    补件
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => openDecision('ESCALATE')}
                  >
                    转平台审核
                  </button>
                </div>
              ) : (
                <p className="text-[11px] text-slate-400 pb-6">
                  仅「商户待审」状态的申请可执行审核决策。
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ============ 决策二次确认弹窗 ============ */}
      <Modal
        open={decision !== null}
        title={decision ? DECISION_META[decision].title : ''}
        onClose={closeDecision}
        footer={
          <>
            <button className="btn-secondary" disabled={submitting} onClick={closeDecision}>
              取消
            </button>
            <button
              className={decision ? DECISION_META[decision].buttonClass : 'btn-primary'}
              disabled={submitting}
              onClick={() => void submitDecision()}
            >
              {submitting ? '提交中…' : decision ? DECISION_META[decision].confirmText : '确认'}
            </button>
          </>
        }
      >
        {detail && decision && (
          <div className="space-y-3 text-sm">
            <div className="bg-slate-50 rounded-lg p-3 space-y-1">
              <div className="flex justify-between">
                <span className="text-slate-400 text-xs">企业</span>
                <span className="font-medium">{detail.corpName || detail.legalName || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400 text-xs">注册号</span>
                <span className="font-mono">{detail.regNoMask || '—'}</span>
              </div>
            </div>

            {decision === 'APPROVE' && (
              <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 rounded-lg p-3">
                <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  通过后将创建企业主账户与默认管理员，并自动上报平台备案（备案失败不阻塞本地开通，
                  可在详情中重新上报）。请确认材料真实齐备。
                </span>
              </div>
            )}

            {(decision === 'REJECT' || decision === 'SUPPLEMENT') && (
              <div>
                <label className="label">
                  {decision === 'REJECT' ? '驳回原因' : '补件说明'}{' '}
                  <span className="text-red-500">*</span>
                </label>
                <textarea
                  className="input min-h-[72px]"
                  maxLength={600}
                  placeholder={
                    decision === 'REJECT'
                      ? '如：执照影像模糊，请重新上传（原因对企可见，1-500 字）'
                      : '如：请补充企业授权书原件（说明对企可见，1-500 字）'
                  }
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  必填 1-500 字{reason.trim().length > 0 && `，当前 ${reason.trim().length} 字`}
                </p>
              </div>
            )}

            {decision === 'ESCALATE' && (
              <div className="flex items-start gap-2 text-xs text-purple-800 bg-purple-50 rounded-lg p-3">
                <ShieldAlert className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  转平台终审后申请将由平台复核（高风险行业必经路径），商户侧不再决策。
                </span>
              </div>
            )}
          </div>
        )}
      </Modal>
    </PageContainer>
  );
}
